import { useMemo, useState } from "react";

import {
  bajaAutorizacion,
  editarAutorizacion,
  reactivarAutorizacion,
} from "../../services/vigilancia";
import {
  VISTAS,
  avisoDeError,
  claseAutorizado,
  contarPorVista,
  detalleDe,
  enVista,
  esPersonal,
  esVigente,
  filtrarPorPatenteONombre,
  ordenarAutorizados,
  otraConPatente,
  patenteBusqueda,
} from "../../shared/autosAutorizados";
import { hoyAr } from "../../shared/fechaAr";
import Patente from "../funcionales/Patente";
import AgregarAutorizado from "./AgregarAutorizado";
import FotosAutorizacion from "./FotosAutorizacion";

/* ── Autorizados: UN solo listado (Nico, ticket 441, 19/10) ─────────────────
 *
 * «Autorización de autos se ve engorrosamente difícil. ¿No puede haber un solo
 * listado y un solo lugar para agregar gente? En ese listado que se distingan
 * los temporales o los dados de baja con otro color o algo distinto».
 *
 * Eran tres listas —«Autos del personal», «Autorizados de antemano» y, plegada,
 * «No vigentes»— con dos formularios. Ahora todos van acá, con UN «Agregar».
 * Cada renglón dice cómo vale con una pastilla CON TEXTO (el color no es la
 * única señal) y la leyenda de arriba la explica:
 *
 *   fijo          verde, sin vencimiento.
 *   temporal      azul, «hasta el 25/10» (o «hasta hoy»), con la franja azul.
 *   vencido       rojo, «venció el 15/09», el renglón apagado.
 *   dado de baja  gris, el renglón apagado y el nombre tachado; quién y cuándo.
 *
 * Primero los que entran, por nombre; después los vencidos y al final las
 * bajas. Se busca por patente o nombre y se filtra con Todos · Vigentes ·
 * Temporales · Bajas. De las vencidas y dadas de baja llegan las más recientes
 * (`TOPE_NO_VIGENTES`); las más viejas, con el buscador.
 *
 * ── QUIÉN HACE QUÉ: LOS PERMISOS DE SIEMPRE ───────────────────────────────
 *
 * `cargar.vigilancia` (la barrera, Guillermo 13/10) agrega, corrige, da de
 * baja y reactiva los autos de la casa (`personal`); `autorizar.vigilancia`,
 * además, todo lo de afuera (temporales, motivo, hasta cuándo vale). El que
 * sólo mira ve la lista sin botones. El candado real lo pone la RLS: acá no se
 * muestra lo que la base rebotaría.
 *
 * Cada renglón tiene UN botón —«Editar» o, si está dado de baja,
 * «Reactivar»— y «Dar de baja» vive adentro de la edición: en el celular de la
 * barrera dos botones por renglón le comen el ancho al nombre. */

/* «2026-09-30» → «30/09/2026», sin `Date`: es un día, no un instante. */
const diaCorto = (f) => (f ? String(f).slice(0, 10).split("-").reverse().join("/") : "");

/* «nico@cantarini.com.ar» → «nico»: la base sella con el mail de la sesión, y
 * en una celda alcanza con quién es. Un nombre (lo que manda el bot) queda tal
 * cual. */
const quienCorto = (v) => String(v ?? "").split("@")[0].trim();

const TZ = "America/Argentina/Buenos_Aires";
const fechaHora = (iso) =>
  iso
    ? new Date(iso).toLocaleString("es-AR", {
        timeZone: TZ,
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/* Los sectores que ya se usaron, para elegir en vez de escribir. */
const sectoresDe = (filas) =>
  [...new Set((filas || []).map((a) => String(a.sector ?? "").trim()).filter(Boolean))].sort(
    (x, y) => x.localeCompare(y, "es"),
  );

/* Cómo vale: la pastilla (con texto) y, abajo, la fecha o quién la dio de baja. */
const Vale = ({ a, clase, hoy }) => {
  if (clase === "fijo") return <span className="pill ok">fijo</span>;
  if (clase === "temporal") {
    const dia = String(a.vence).slice(0, 10);
    return (
      <>
        <span className="pill info">temporal</span>
        <span className="sub">{dia === hoy ? "hasta hoy" : `hasta el ${diaCorto(dia)}`}</span>
      </>
    );
  }
  if (clase === "vencido") {
    return (
      <>
        <span className="pill mal">vencido</span>
        <span className="sub">{`venció el ${diaCorto(a.vence)}`}</span>
      </>
    );
  }
  return (
    <>
      <span className="pill gris">dado de baja</span>
      {(a.baja_en || a.baja_por) && (
        <span className="sub">
          {a.baja_en ? `el ${fechaHora(a.baja_en)}` : ""}
          {a.baja_por ? ` por ${quienCorto(a.baja_por)}` : ""}
        </span>
      )}
    </>
  );
};

/* Lo que dice cada color, arriba de la lista. */
const Leyenda = () => (
  <ul className="vig-leyenda" aria-label="Qué quiere decir cada color">
    <li>
      <span className="pill ok">fijo</span> sin vencimiento
    </li>
    <li>
      <span className="pill info">temporal</span> hasta una fecha
    </li>
    <li>
      <span className="pill mal">vencido</span> se le pasó la fecha
    </li>
    <li>
      <span className="pill gris">dado de baja</span> lo sacaron de la lista
    </li>
  </ul>
);

/* ── Editar o reactivar un renglón ─────────────────────────────────────────
 *
 * El mismo formulario para las dos cosas. HASTA CUÁNDO VALE se elige sólo en
 * lo de afuera (y lo elige quien autoriza): un auto de la casa es fijo, como
 * siempre. Al reactivar uno al que se le pasó la fecha, arranca en hoy: si no,
 * volvería a la lista ya vencido. */
const FilaEdicion = ({ a, modo, colSpan, vigentes, sectores, puedeAutorizar, onCerrar, onCambio }) => {
  const hoy = hoyAr();
  const personal = esPersonal(a);
  const conFecha = !personal && puedeAutorizar;
  const original = a.vence ? String(a.vence).slice(0, 10) : "";
  const reactivar = modo === "reactivar";
  const [v, setV] = useState(() => ({
    patente: a.patente || "",
    nombre: a.nombre || "",
    detalle: (personal ? a.sector : a.motivo) || "",
    dni: a.dni || "",
    vale: original ? "temporal" : "fijo",
    hasta: original && !(reactivar && original < hoy) ? original : hoy,
  }));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const poner = (c) => setV((x) => ({ ...x, ...c }));
  const idBase = `vig-ed-${a.id}`;

  const guardar = async () => {
    setError(null);
    const p = patenteBusqueda(v.patente);
    const n = v.nombre.trim();
    if (personal ? !p || !n : !p && !n) {
      setError(personal ? "Dejá la patente y el nombre." : "Dejá al menos el nombre o la patente.");
      return;
    }
    let vence;
    if (conFecha) {
      if (v.vale === "temporal") {
        if (!v.hasta) return setError("Poné hasta qué día vale.");
        // Una fecha pasada no habilita a nadie. La que ya tenía se puede dejar
        // (para corregir otra cosa de una vencida); una nueva, no.
        if (v.hasta < hoy && (reactivar || v.hasta !== original)) {
          return setError(
            reactivar
              ? "Esa fecha ya pasó: poné hasta qué día vale."
              : "Esa fecha ya pasó. Para sacarlo ahora, usá «Dar de baja».",
          );
        }
        vence = v.hasta;
      } else {
        vence = "";
      }
    }
    const otra = otraConPatente(vigentes, p, a.id);
    if (otra) {
      setError(`Esa patente ya está en la lista${otra.nombre ? ` (${otra.nombre})` : ""}.`);
      return;
    }
    const cambios = {
      patente: v.patente,
      nombre: v.nombre,
      dni: v.dni,
      ...(personal ? { sector: v.detalle } : { motivo: v.detalle }),
      ...(vence === undefined ? {} : { vence }),
    };
    setGuardando(true);
    try {
      await (reactivar ? reactivarAutorizacion : editarAutorizacion)(a.id, cambios);
      onCerrar();
      onCambio?.();
    } catch (e) {
      setGuardando(false);
      setError(avisoDeError(e, reactivar ? "No se pudo reactivar" : "No se pudo guardar el cambio"));
    }
  };

  const baja = async () => {
    const quien = String(a.nombre ?? "").trim() || "esta autorización";
    const pat = patenteBusqueda(a.patente);
    if (
      !window.confirm(
        `¿Dar de baja a ${quien}${pat ? ` (${pat})` : ""}? Deja de estar autorizado ahora ` +
          "mismo y queda en la lista como dado de baja.",
      )
    )
      return;
    setError(null);
    try {
      await bajaAutorizacion(a.id);
      onCerrar();
      onCambio?.();
    } catch (e) {
      setError(avisoDeError(e, "No se pudo dar de baja"));
    }
  };

  return (
    <tr className="vig-editando" data-ver={a.id}>
      <td colSpan={colSpan}>
        <div className="vig-form">
          <div className="vig-campo">
            <label htmlFor={`${idBase}-patente`}>Patente</label>
            <input
              id={`${idBase}-patente`}
              value={v.patente}
              onChange={(e) => poner({ patente: e.target.value.toUpperCase() })}
              placeholder="AB123CD"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
          </div>
          <div className="vig-campo">
            <label htmlFor={`${idBase}-nombre`}>Nombre</label>
            <input
              id={`${idBase}-nombre`}
              value={v.nombre}
              onChange={(e) => poner({ nombre: e.target.value })}
              placeholder="Apellido, nombre"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div className="vig-campo">
            <label htmlFor={`${idBase}-detalle`}>Sector / motivo</label>
            <input
              id={`${idBase}-detalle`}
              value={v.detalle}
              onChange={(e) => poner({ detalle: e.target.value })}
              autoComplete="off"
              list={`${idBase}-sectores`}
            />
            <datalist id={`${idBase}-sectores`}>
              {sectores.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
          <div className="vig-campo">
            <label htmlFor={`${idBase}-dni`}>DNI (optativo)</label>
            <input
              id={`${idBase}-dni`}
              value={v.dni}
              onChange={(e) => poner({ dni: e.target.value })}
              autoComplete="off"
              inputMode="numeric"
            />
          </div>
          {conFecha && (
            <div className="vig-campo">
              <span id={`${idBase}-vale`}>Vale</span>
              <div className="tk-filtros" role="group" aria-labelledby={`${idBase}-vale`}>
                {[
                  ["fijo", "Fijo"],
                  ["temporal", "Temporal"],
                ].map(([id, texto]) => (
                  <button
                    key={id}
                    type="button"
                    className={`tk-chip${v.vale === id ? " activo" : ""}`}
                    aria-pressed={v.vale === id}
                    onClick={() => poner({ vale: id })}
                  >
                    {texto}
                  </button>
                ))}
              </div>
            </div>
          )}
          {conFecha && v.vale === "temporal" && (
            <div className="vig-campo">
              <label htmlFor={`${idBase}-hasta`}>Hasta</label>
              <input
                id={`${idBase}-hasta`}
                type="date"
                value={v.hasta}
                min={hoy}
                onChange={(e) => poner({ hasta: e.target.value })}
              />
            </div>
          )}
          <div className="vig-campo">
            <span>Foto</span>
            <FotosAutorizacion autorizacion={a} puedeSubir={!reactivar} onSubida={onCambio} />
          </div>
        </div>
        {error && <p className="panel-aviso">{error}</p>}
        <div className="vig-acciones">
          {/* «Dar de baja» a la izquierda, lejos de «Guardar»: es el que no se
              deshace con un toque. */}
          {!reactivar && (
            <button type="button" className="btn btn--ghost vig-sm vig-baja" onClick={baja}>
              Dar de baja
            </button>
          )}
          <button type="button" className="btn btn--ghost vig-sm" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="button" className="btn vig-sm" disabled={guardando} onClick={guardar}>
            {guardando ? "Guardando…" : reactivar ? "Reactivar" : "Guardar"}
          </button>
        </div>
      </td>
    </tr>
  );
};

const VACIO_DE_VISTA = {
  todos: "Todavía no hay nadie en la lista.",
  vigentes: "No hay autorizados vigentes.",
  temporales: "No hay temporales.",
  bajas: "No hay dados de baja.",
};

const Autorizados = ({
  filas = [],
  recientes = [],
  puedeCargar = false,
  puedeAutorizar = false,
  onCambio,
  vista = "todos",
  onVista,
  busqueda = "",
  onBusqueda,
  truncadas = false,
  tope = 50,
}) => {
  const hoy = hoyAr();
  const [agregando, setAgregando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [edicion, setEdicion] = useState(null); // { id, modo }

  const puedeAgregar = puedeCargar || puedeAutorizar;
  const puedeEditarFila = (a) => puedeAutorizar || (esPersonal(a) && puedeCargar);

  const clases = useMemo(
    () => new Map(filas.map((a) => [a.id, claseAutorizado(a, hoy)])),
    [filas, hoy],
  );
  const conteos = useMemo(() => contarPorVista(filas, hoy), [filas, hoy]);
  const vigentes = useMemo(() => filas.filter((a) => esVigente(clases.get(a.id))), [filas, clases]);
  const sectores = useMemo(() => sectoresDe(filas), [filas]);
  const visibles = useMemo(
    () =>
      filtrarPorPatenteONombre(
        ordenarAutorizados(filas, hoy).filter((a) => enVista(vista, clases.get(a.id))),
        busqueda,
      ),
    [filas, hoy, vista, clases, busqueda],
  );

  /* Mostrar uno en la lista: el que ya estaba cuando se lo quiso agregar. */
  const mostrar = (a) => {
    onVista?.("todos");
    onBusqueda?.(patenteBusqueda(a?.patente) || String(a?.nombre ?? "").trim());
  };

  const columnas = 6 + (puedeAgregar ? 1 : 0);

  return (
    <section className="vig-autorizados" aria-label="Autorizados">
      <div className="vig-novig-cab">
        <h3 className="docs-sub">Autorizados · {conteos.vigentes}</h3>
        {puedeAgregar && !agregando && (
          <button
            type="button"
            className="tk-chip"
            onClick={() => {
              setAgregando(true);
              setAviso("");
            }}
          >
            Agregar
          </button>
        )}
      </div>

      {puedeAgregar && agregando && (
        <AgregarAutorizado
          filas={filas}
          recientes={recientes}
          sectores={sectores}
          puedeAutorizar={puedeAutorizar}
          onCancelar={() => setAgregando(false)}
          onMostrar={mostrar}
          onAgregado={(texto) => {
            setAgregando(false);
            setAviso(texto);
            onCambio?.();
          }}
        />
      )}
      {aviso && (
        <p className="panel-aviso suave" role="status">
          {aviso}
        </p>
      )}

      <Leyenda />

      <div className="tk-filtros vig-vistas" role="group" aria-label="Qué mostrar">
        {VISTAS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`tk-chip${vista === v.id ? " activo" : ""}`}
            aria-pressed={vista === v.id}
            onClick={() => onVista?.(v.id)}
          >
            {v.label} · {conteos[v.id]}
          </button>
        ))}
      </div>
      <input
        className="vig-filtro"
        type="search"
        value={busqueda}
        onChange={(e) => onBusqueda?.(e.target.value)}
        placeholder="Buscar por patente o nombre"
        aria-label="Buscar en autorizados"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />

      {visibles.length === 0 ? (
        <p className="panel-aviso suave">
          {busqueda.trim()
            ? `Nadie de la lista coincide con «${busqueda.trim()}».`
            : VACIO_DE_VISTA[vista] || VACIO_DE_VISTA.todos}
        </p>
      ) : (
        <div className="tabla-wrap">
          <table className="tabla fija vig-tabla vig-cards vig-autorizados-tabla">
            <thead>
              <tr>
                <th>Patente</th>
                <th>Nombre</th>
                <th>Sector / motivo</th>
                <th>Vale</th>
                <th>Autorizó</th>
                <th>Foto</th>
                {puedeAgregar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {visibles.map((a) => {
                const clase = clases.get(a.id);
                const editable = puedeEditarFila(a);
                if (edicion && String(edicion.id) === String(a.id) && editable) {
                  return (
                    <FilaEdicion
                      key={a.id}
                      a={a}
                      modo={edicion.modo}
                      colSpan={columnas}
                      vigentes={vigentes}
                      sectores={sectores}
                      puedeAutorizar={puedeAutorizar}
                      onCerrar={() => setEdicion(null)}
                      onCambio={onCambio}
                    />
                  );
                }
                const fotos = Array.isArray(a.fotos) ? a.fotos : [];
                // La foto de uno que no se puede editar la sigue sacando la
                // barrera desde el renglón (ticket 290); a los que se editan,
                // adentro de «Editar».
                const subeDesdeAca = esVigente(clase) && puedeCargar && !editable;
                return (
                  <tr key={a.id} data-ver={a.id} className={`vig-k-${clase}`}>
                    <td className="vig-c-patente">
                      {a.patente ? <Patente dominio={a.patente} /> : <span className="sin-dato">—</span>}
                    </td>
                    <td className="vig-c-quien">
                      <b>{a.nombre || <span className="sin-dato">—</span>}</b>
                      {a.dni && <span className="sub">DNI {a.dni}</span>}
                    </td>
                    <td className="vig-c-detalle">
                      {detalleDe(a) || <span className="sin-dato">—</span>}
                    </td>
                    <td className="vig-c-vale">
                      <Vale a={a} clase={clase} hoy={hoy} />
                    </td>
                    <td className="tenue vig-c-autorizo">
                      {quienCorto(a.autorizado_por)}
                      {/* Lo que completó la barrera al llegar (ticket 521). */}
                      {a.completado?.length > 0 && (
                        <span className="sub">
                          {a.completado.map((c) => (c === "dni" ? "DNI" : "patente")).join(" y ")}
                          {" de la barrera"}
                          {quienCorto(a.completado_por) ? ` (${quienCorto(a.completado_por)})` : ""}
                        </span>
                      )}
                    </td>
                    <td className={`vig-c-foto${fotos.length || subeDesdeAca ? "" : " vig-vacio"}`}>
                      {fotos.length || subeDesdeAca ? (
                        <FotosAutorizacion
                          autorizacion={a}
                          puedeSubir={subeDesdeAca}
                          onSubida={onCambio}
                        />
                      ) : (
                        <span className="sin-dato">—</span>
                      )}
                    </td>
                    {puedeAgregar && (
                      <td className="vig-edit-acc vig-c-accion">
                        {editable && (
                          <button
                            type="button"
                            className="btn btn--ghost vig-sm"
                            onClick={() =>
                              setEdicion({ id: a.id, modo: clase === "baja" ? "reactivar" : "editar" })
                            }
                          >
                            {clase === "baja" ? "Reactivar" : "Editar"}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {truncadas && vista !== "vigentes" && (
            <p className="docs-pie">
              De los vencidos y dados de baja se ven los {tope} más recientes. Los más viejos se
              encuentran con el buscador.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default Autorizados;
